/**
 * Housekeeping Dashboard
 * Dedicated interface for housekeeping staff
 * Features: Room status board, task queue, event notifications, Marina chat
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Sparkles, 
  ClipboardList, 
  CheckCircle,
  BedDouble,
  Droplets,
  AlertCircle,
  Wrench,
  User,
  Clock,
  RefreshCw,
  Bell,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { MarinaChat } from '@/components/MarinaChat';
import { useStaffAuth } from '@/hooks/useStaffAuth';
import { BACKEND_URL } from '@/lib/backendUrl';


export default function HousekeepingDashboardPage() {
  // Authentication & Authorization
  const { user, loading: authLoading, hasAccess, logout, hotelId: authHotelId } = useStaffAuth(['housekeeping', 'manager', 'owner']);
  
  const [loading, setLoading] = useState(true);
  const [roomStatus, setRoomStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showMarina, setShowMarina] = useState(false);
  const hotelId = authHotelId || 'hotel_demo_001'; // Use auth context or fallback

  // Auto-refresh every 30 seconds
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadRoomStatus(),
      loadTasks(),
      loadEvents()
    ]);
  };

  const loadRoomStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/room-status`);
      const data = await response.json();
      if (data.success) {
        setRoomStatus(data.summary);
      }
    } catch (error) {
      console.error('Failed to load room status:', error);
    }
  };

  const loadTasks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/housekeeping`);
      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/events/stream?hotel_id=${hotelId}&target_department=housekeeping&status=pending&limit=20`
      );
      const data = await response.json();
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const updateRoomStatus = async (roomId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/room-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: roomId,
          status: newStatus
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Room ${roomId} marked as ${newStatus}`);
        loadData(); // Refresh all data
      } else {
        toast.error(data.error || 'Failed to update room status');
      }
    } catch (error) {
      console.error('Failed to update room status:', error);
      toast.error('Failed to update room status');
    }
  };

  const completeEvent = async (eventId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completed_by: 'housekeeping_staff',
          result_data: {
            action_taken: 'Task completed from housekeeping dashboard',
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Task completed! Analytics logged.');
        loadEvents();
      }
    } catch (error) {
      console.error('Failed to complete event:', error);
      toast.error('Failed to complete event');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="lobby-theme min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  // Access denied screen
  if (!hasAccess) {
    return (
      <div className="lobby-theme min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You don't have permission to access the Housekeeping Dashboard.
              {user && (
                <span className="block mt-2">
                  Your role: <strong>{user.role}</strong>
                </span>
              )}
            </p>
            <Button onClick={logout} variant="outline" className="w-full">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  let filteredTasks = tasks;
  if (statusFilter) {
    filteredTasks = tasks.filter(t => t.status === statusFilter);
  }

  const urgentTasks = tasks.filter(t => t.priority === 'urgent');
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedToday = tasks.filter(t => t.status === 'completed');

  return (
    <div className="lobby-theme min-h-screen">
      {/* Header */}
      <div className="border-b border-[color:var(--l-line)] bg-[color:var(--l-bg-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/10">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl heading-font tracking-tight">
                  Housekeeping Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Room status • Tasks • Marina alerts
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {user && (
                <div className="hidden md:flex items-center gap-2 mr-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{user.name}</span>
                  <Badge variant="secondary" className="capitalize">{user.role}</Badge>
                </div>
              )}
              
              {events.length > 0 && (
                <Badge variant="destructive" className="animate-pulse">
                  <Bell className="w-3 h-3 mr-1" />
                  {events.length} New Alert{events.length !== 1 ? 's' : ''}
                </Badge>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              <Button
                onClick={() => setShowMarina(!showMarina)}
                data-testid="toggle-marina-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Marina AI
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Room Status Summary Board */}
        {roomStatus && (
          <div>
            <h2 className="text-xl heading-font mb-4 flex items-center gap-2">
              <BedDouble className="w-5 h-5" />
              Room Status Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl heading-font tabular-nums text-[hsl(var(--status-clean))]">
                    {roomStatus.clean || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Clean</p>
                </CardContent>
              </Card>

              <Card className="border-destructive/30 bg-destructive/5">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl heading-font tabular-nums text-destructive">
                    {roomStatus.dirty || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Dirty</p>
                </CardContent>
              </Card>

              <Card className="border-[hsl(38_92%_55%)]/30 bg-[hsl(38_92%_55%)]/5">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl heading-font tabular-nums text-[hsl(38_92%_55%)]">
                    {roomStatus.occupied || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Occupied</p>
                </CardContent>
              </Card>

              <Card className="border-[hsl(var(--status-maintenance))]/30 bg-[hsl(var(--status-maintenance))]/5">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl heading-font tabular-nums text-[hsl(var(--status-maintenance))]">
                    {roomStatus.maintenance || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Maintenance</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl heading-font tabular-nums text-muted-foreground">
                    {roomStatus.vacant || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Vacant</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Pending Event Notifications */}
        {events.length > 0 && (
          <div>
            <h2 className="text-xl heading-font mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              New Alerts from Marina
            </h2>
            <div className="space-y-3">
              {events.map((event) => (
                <Alert
                  key={event.event_id}
                  className="border-primary/30 bg-primary/5"
                  data-testid={`event-alert-${event.event_id}`}
                >
                  <Sparkles className="w-4 h-4 text-primary" />
                  <AlertDescription>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-sm">
                          {formatEventType(event.event_type)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {renderEventSummary(event)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => completeEvent(event.event_id)}
                        data-testid={`complete-event-${event.event_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Done
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        )}

        {/* Task Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-destructive/20 hover:border-destructive/40" 
            onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
            data-testid="urgent-tasks-card"
          >
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
              <p className="text-4xl heading-font tabular-nums text-destructive">{urgentTasks.length}</p>
              <p className="text-sm text-muted-foreground mt-1">Urgent</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-primary/20 hover:border-primary/40"
            onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
            data-testid="pending-tasks-card"
          >
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-4xl heading-font tabular-nums text-primary">{pendingTasks.length}</p>
              <p className="text-sm text-muted-foreground mt-1">Pending</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-[hsl(38_92%_55%)]/20 hover:border-[hsl(38_92%_55%)]/40"
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
            data-testid="in-progress-tasks-card"
          >
            <CardContent className="p-6 text-center">
              <Droplets className="w-8 h-8 text-[hsl(38_92%_55%)] mx-auto mb-2" />
              <p className="text-4xl heading-font tabular-nums text-[hsl(38_92%_55%)]">{inProgressTasks.length}</p>
              <p className="text-sm text-muted-foreground mt-1">In Progress</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/20"
            onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
            data-testid="completed-tasks-card"
          >
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-8 h-8 text-[hsl(var(--status-clean))] mx-auto mb-2" />
              <p className="text-4xl heading-font tabular-nums text-[hsl(var(--status-clean))]">{completedToday.length}</p>
              <p className="text-sm text-muted-foreground mt-1">Completed Today</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filter:</span>
          <Badge 
            variant={statusFilter === null ? 'default' : 'outline'} 
            className="cursor-pointer"
            onClick={() => setStatusFilter(null)}
            data-testid="filter-all"
          >
            All ({tasks.length})
          </Badge>
          <Badge 
            variant={statusFilter === 'pending' ? 'default' : 'outline'} 
            className="cursor-pointer"
            onClick={() => setStatusFilter('pending')}
            data-testid="filter-pending"
          >
            Pending
          </Badge>
          <Badge 
            variant={statusFilter === 'in_progress' ? 'default' : 'outline'} 
            className="cursor-pointer"
            onClick={() => setStatusFilter('in_progress')}
            data-testid="filter-in-progress"
          >
            In Progress
          </Badge>
          <Badge 
            variant={statusFilter === 'completed' ? 'default' : 'outline'} 
            className="cursor-pointer"
            onClick={() => setStatusFilter('completed')}
            data-testid="filter-completed"
          >
            Completed
          </Badge>
        </div>

        {/* Task Grid */}
        <div>
          <h2 className="text-xl heading-font mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Cleaning Tasks ({filteredTasks.length})
          </h2>

          {filteredTasks.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="heading-font text-lg mb-2">No Tasks</h3>
                <p className="text-sm text-muted-foreground">
                  {statusFilter ? `No ${statusFilter} tasks` : 'All rooms are clean! Great work! 🎉'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTasks.map(task => (
                <Card 
                  key={task.id} 
                  className={`hover:shadow-lg transition-all ${
                    task.priority === 'urgent' 
                      ? 'border-destructive/30 bg-destructive/5' 
                      : task.priority === 'high'
                      ? 'border-[hsl(38_92%_55%)]/30 bg-[hsl(38_92%_55%)]/5'
                      : ''
                  }`}
                  data-testid={`task-card-${task.room_number}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl heading-font tabular-nums">
                          Room {task.room_number}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1 capitalize">
                          {task.task_type.replace('_', ' ')}
                        </p>
                      </div>
                      <Badge 
                        variant={
                          task.priority === 'urgent' ? 'destructive' : 
                          task.priority === 'high' ? 'warning' : 
                          'outline'
                        }
                        className="capitalize"
                      >
                        {task.priority}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <Badge 
                        variant={task.status === 'completed' ? 'default' : 'outline'}
                        className="capitalize"
                      >
                        {task.status.replace('_', ' ')}
                      </Badge>
                    </div>

                    {task.assigned_to && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Assigned
                        </span>
                        <span className="font-medium">{task.assigned_to}</span>
                      </div>
                    )}

                    {task.notes && (
                      <div className="p-3 rounded-lg bg-muted/50 border">
                        <p className="text-xs text-muted-foreground mb-1">Notes:</p>
                        <p className="text-sm">{task.notes}</p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="pt-2 space-y-2">
                      {task.status !== 'completed' && (
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => updateRoomStatus(task.room_id, 'clean')}
                          data-testid={`mark-clean-${task.room_number}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Clean
                        </Button>
                      )}

                      {task.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => updateRoomStatus(task.room_id, 'maintenance')}
                          data-testid={`report-issue-${task.room_number}`}
                        >
                          <Wrench className="w-4 h-4 mr-2" />
                          Report Issue
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Marina Chat */}
      {showMarina && (
        <MarinaChat 
          department="housekeeping"
          onClose={() => setShowMarina(false)}
        />
      )}
    </div>
  );
}

// ─── UTILITY FUNCTIONS ───

function formatEventType(eventType) {
  return eventType
    .split('.')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' › ');
}

function renderEventSummary(event) {
  const payload = event.payload;
  
  if (event.event_type === 'loyalty.perk_issued') {
    return `Guest ${payload.guest_email} unlocked ${payload.perk_type} - ${payload.reward}`;
  }
  
  if (event.event_type === 'ops.housekeeping_task_created') {
    return `Room ${payload.room_number}: ${payload.task_type} (${payload.priority} priority)`;
  }
  
  return JSON.stringify(payload);
}
